import React, { useState } from 'react';
import { ExportConfig, RecordingSession } from '@/types';
import { exportService, ExportProgressUpdate } from '@/services/exportService';
import confetti from 'canvas-confetti';
import {
  Share2,
  FileArchive,
  Film,
  Users,
  Mic,
  Subtitles,
  Folder,
  CheckCircle2,
  Loader2,
  Sparkles,
  ArrowRight,
  FolderOpen,
  Download
} from 'lucide-react';

interface ExportCenterScreenProps {
  session: RecordingSession | null;
  defaultOutputFolder: string;
}

export const ExportCenterScreen: React.FC<ExportCenterScreenProps> = ({
  session,
  defaultOutputFolder
}) => {
  const [config, setConfig] = useState<ExportConfig>({
    outputFolder: defaultOutputFolder,
    exportOriginal: true,
    exportIndividualParticipants: true,
    exportSpeakerFocused: true,
    exportZipBundle: true,
    exportSrtSubtitles: true,
    quality: 'source',
    videoCodec: 'h264'
  });

  const [isExporting, setIsExporting] = useState(false);
  const [isExportComplete, setIsExportComplete] = useState(false);
  const [progressUpdate, setProgressUpdate] = useState<ExportProgressUpdate>({
    phase: 'Idle',
    currentFile: '',
    progressPercent: 0,
    completedFiles: []
  });

  const handleDownloadRaw = async (filePath?: string, blobUrl?: string, customName?: string) => {
    const filename = customName || `${session?.title.replace(/[^a-zA-Z0-9_-]/g, '_') || 'Recording'}.mp4`;
    if (blobUrl) {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
    if (window.electronAPI && filePath) {
      try {
        const buf = await window.electronAPI.readRecordingFile(filePath);
        const blob = new Blob([buf], { type: 'video/mp4' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 2000);
        return;
      } catch {
        window.electronAPI.showItemInFolder(filePath);
      }
    }
  };

  const handleRevealInFolder = (filePath?: string) => {
    const target = filePath || config.outputFolder;
    if (window.electronAPI && target) {
      window.electronAPI.showItemInFolder(target);
    }
  };

  if (!session) {
    return (
      <div className="flex-1 overflow-y-auto p-8 flex flex-col items-center justify-center text-center space-y-4">
        <Film className="w-12 h-12 text-brand-muted" />
        <h2 className="text-xl font-bold text-brand-text">No Recording Available for Export</h2>
        <p className="text-xs text-brand-muted">Please record a meeting session before exporting deliverables.</p>
      </div>
    );
  }

  const handleSelectOutputFolder = async () => {
    if (window.electronAPI && typeof window.electronAPI.selectDirectory === 'function') {
      const selected = await window.electronAPI.selectDirectory(config.outputFolder);
      if (selected) {
        setConfig(prev => ({ ...prev, outputFolder: selected }));
      }
    }
  };

  const handleStartExport = async () => {
    setIsExporting(true);
    setIsExportComplete(false);

    try {
      const result = await exportService.executeExport(session, config, (update) => {
        setProgressUpdate(update);
      });

      if (result.success) {
        setIsExportComplete(true);
        confetti({
          particleCount: 80,
          spread: 60,
          origin: { y: 0.7 }
        });
      }
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleOpenFolder = () => {
    if (window.electronAPI) {
      window.electronAPI.openPath(config.outputFolder);
    }
  };

  const includedParticipants = session.participants.filter(p => p.isIncluded);

  return (
    <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-brand-border/60">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-brand-text">Export Center</h1>
          <p className="text-xs text-brand-muted mt-0.5">
            Package and render separate participant MP4 clips with localized audio
          </p>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs px-2.5 py-1 rounded-xl bg-brand-surface border border-brand-border text-brand-muted font-medium">
            Meeting: <strong className="text-brand-text">{session.title}</strong>
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT: Export Options & Form (7 Columns) */}
        <div className="lg:col-span-7 space-y-5">
          {/* Direct Raw MP4 Video Downloads Card */}
          <div className="p-4 rounded-2xl bg-gradient-to-br from-brand-surface to-brand-elevated border border-brand-border shadow-card space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-brand-text uppercase tracking-wider flex items-center gap-1.5">
                <Film className="w-3.5 h-3.5 text-brand-accent" />
                <span>Direct Raw MP4 Video Files</span>
              </h3>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 font-bold">
                Real Video Files (.mp4)
              </span>
            </div>

            <p className="text-xs text-brand-muted">
              Download the real recorded MP4 video files directly to your PC:
            </p>

            <div className="space-y-2">
              {/* Primary / Host File */}
              <div className="p-3 rounded-xl bg-brand-surface border border-brand-border/60 flex items-center justify-between gap-3 text-xs">
                <div className="truncate">
                  <span className="font-semibold text-brand-text block truncate">
                    {session.podcastMetadata?.hostName ? `Host Video Track: ${session.podcastMetadata.hostName}` : 'Master Video Track (.mp4)'}
                  </span>
                  <span className="font-mono text-[10px] text-brand-muted truncate block">
                    {session.filePath || 'Real MP4 Video'}
                  </span>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => handleDownloadRaw(session.filePath, session.blobUrl, `${session.title}_Master.mp4`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-accent hover:bg-brand-accent-hover text-white text-xs font-semibold shadow-sm transition-all"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Download MP4</span>
                  </button>
                  {session.filePath && (
                    <button
                      onClick={() => handleRevealInFolder(session.filePath)}
                      className="p-1.5 rounded-lg bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border text-brand-muted hover:text-brand-text"
                      title="Show in Windows Explorer"
                    >
                      <FolderOpen className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Guest File if available */}
              {session.exportedClips && session.exportedClips.length > 1 && (
                <div className="p-3 rounded-xl bg-emerald-950/20 border border-emerald-500/30 flex items-center justify-between gap-3 text-xs">
                  <div className="truncate">
                    <span className="font-semibold text-emerald-300 block truncate">
                      {session.podcastMetadata?.guestName ? `Guest Video Track: ${session.podcastMetadata.guestName}` : 'Guest Video Track (.mp4)'}
                    </span>
                    <span className="font-mono text-[10px] text-emerald-400/70 truncate block">
                      {session.exportedClips[1].filePath || 'Guest Raw MP4'}
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => handleDownloadRaw(session.exportedClips![1].filePath, undefined, `${session.title}_Guest.mp4`)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm transition-all"
                    >
                      <Download className="w-3.5 h-3.5" />
                      <span>Download Guest MP4</span>
                    </button>
                    {session.exportedClips[1].filePath && (
                      <button
                        onClick={() => handleRevealInFolder(session.exportedClips![1].filePath)}
                        className="p-1.5 rounded-lg bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border text-brand-muted hover:text-brand-text"
                        title="Show in Windows Explorer"
                      >
                        <FolderOpen className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Deliverables Checklist Card */}
          <div className="p-4 rounded-2xl bg-brand-surface border border-brand-border shadow-card space-y-3">
            <h3 className="text-xs font-bold text-brand-text uppercase tracking-wider">
              Export Deliverables
            </h3>

            <div className="space-y-2.5">
              {/* Individual Participant Clips */}
              <label className="p-3 rounded-xl bg-brand-elevated/40 hover:bg-brand-elevated/70 border border-brand-border/40 flex items-start justify-between cursor-pointer transition-colors">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-brand-accent/20 text-brand-accent mt-0.5">
                    <Users className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-brand-text">
                      Separate Participant MP4 Clips ({includedParticipants.length} people)
                    </h4>
                    <p className="text-[11px] text-brand-muted mt-0.5">
                      Renders cropped individual video feeds for each participant with synchronized meeting audio.
                    </p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={config.exportIndividualParticipants}
                  onChange={(e) => setConfig({ ...config, exportIndividualParticipants: e.target.checked })}
                  className="accent-brand-accent w-4 h-4 rounded cursor-pointer mt-1"
                />
              </label>

              {/* Master Screen Recording */}
              <label className="p-3 rounded-xl bg-brand-elevated/40 hover:bg-brand-elevated/70 border border-brand-border/40 flex items-start justify-between cursor-pointer transition-colors">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-brand-secondary/20 text-brand-secondary mt-0.5">
                    <Film className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-brand-text">Full Master Screen Recording</h4>
                    <p className="text-[11px] text-brand-muted mt-0.5">
                      Preserves the original unmodified meeting screen recording in high bitrate.
                    </p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={config.exportOriginal}
                  onChange={(e) => setConfig({ ...config, exportOriginal: e.target.checked })}
                  className="accent-brand-accent w-4 h-4 rounded cursor-pointer mt-1"
                />
              </label>

              {/* Speaker-Focused Clip */}
              <label className="p-3 rounded-xl bg-brand-elevated/40 hover:bg-brand-elevated/70 border border-brand-border/40 flex items-start justify-between cursor-pointer transition-colors">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/20 text-purple-400 mt-0.5">
                    <Mic className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-brand-text">Active Speaker Highlight Montage</h4>
                    <p className="text-[11px] text-brand-muted mt-0.5">
                      Montage dynamically highlighting visible speakers during active talking segments.
                    </p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={config.exportSpeakerFocused}
                  onChange={(e) => setConfig({ ...config, exportSpeakerFocused: e.target.checked })}
                  className="accent-brand-accent w-4 h-4 rounded cursor-pointer mt-1"
                />
              </label>

              {/* Subtitles & Timestamps */}
              <label className="p-3 rounded-xl bg-brand-elevated/40 hover:bg-brand-elevated/70 border border-brand-border/40 flex items-start justify-between cursor-pointer transition-colors">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400 mt-0.5">
                    <Subtitles className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-brand-text">SRT Timestamp & Activity File</h4>
                    <p className="text-[11px] text-brand-muted mt-0.5">
                      Synchronized SRT subtitles containing participant activity time intervals.
                    </p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={config.exportSrtSubtitles}
                  onChange={(e) => setConfig({ ...config, exportSrtSubtitles: e.target.checked })}
                  className="accent-brand-accent w-4 h-4 rounded cursor-pointer mt-1"
                />
              </label>

              {/* ZIP Archive Bundle */}
              <label className="p-3 rounded-xl bg-brand-elevated/40 hover:bg-brand-elevated/70 border border-brand-border/40 flex items-start justify-between cursor-pointer transition-colors">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/20 text-amber-300 mt-0.5">
                    <FileArchive className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-brand-text">Package ZIP Archive</h4>
                    <p className="text-[11px] text-brand-muted mt-0.5">
                      Compresses all participant assets, manifests, and subtitles into a single ready-to-share ZIP.
                    </p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={config.exportZipBundle}
                  onChange={(e) => setConfig({ ...config, exportZipBundle: e.target.checked })}
                  className="accent-brand-accent w-4 h-4 rounded cursor-pointer mt-1"
                />
              </label>
            </div>
          </div>

          {/* Destination Path Selector */}
          <div className="p-4 rounded-2xl bg-brand-surface border border-brand-border shadow-card space-y-2">
            <label className="text-xs font-bold text-brand-text uppercase tracking-wider flex items-center gap-2">
              <Folder className="w-3.5 h-3.5 text-brand-secondary" />
              Export Destination Folder
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={config.outputFolder}
                onChange={(e) => setConfig({ ...config, outputFolder: e.target.value })}
                className="flex-1 bg-brand-elevated border border-brand-border rounded-xl px-3 py-2 text-xs text-brand-text font-mono focus:outline-none"
              />
              <button
                onClick={handleSelectOutputFolder}
                className="px-4 py-2 bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border rounded-xl text-xs font-medium text-brand-text"
              >
                Browse...
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT: Export Execution & Live Progress (5 Columns) */}
        <div className="lg:col-span-5 space-y-5">
          <div className="p-6 rounded-3xl bg-brand-surface border border-brand-border shadow-elevated space-y-6">
            <div>
              <h3 className="text-base font-bold text-brand-text">Export Summary</h3>
              <p className="text-xs text-brand-muted mt-0.5">
                Target Format: MP4 (H.264 / AAC Audio)
              </p>
            </div>

            {/* Live Progress Bar if running */}
            {isExporting ? (
              <div className="space-y-4 p-4 rounded-2xl bg-brand-elevated/60 border border-brand-border">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-brand-text flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 text-brand-accent animate-spin" />
                    {progressUpdate.phase}
                  </span>
                  <span className="font-mono text-brand-secondary tabular-nums">
                    {progressUpdate.progressPercent}%
                  </span>
                </div>

                <div className="w-full h-2.5 bg-brand-elevated rounded-full overflow-hidden border border-brand-border">
                  <div
                    className="h-full bg-gradient-to-r from-brand-accent to-brand-secondary rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(100, progressUpdate.progressPercent)}%` }}
                  />
                </div>

                <p className="text-[11px] font-mono text-brand-muted truncate">
                  Processing: {progressUpdate.currentFile}
                </p>
              </div>
            ) : isExportComplete ? (
              <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span className="text-xs font-bold">Export Successfully Generated!</span>
                </div>
                <p className="text-[11px] text-brand-muted">
                  All individual participant MP4 clips and archives are saved on your local storage drive.
                </p>

                <button
                  onClick={handleOpenFolder}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-xs font-semibold text-emerald-300 transition-colors"
                >
                  <FolderOpen className="w-3.5 h-3.5" />
                  <span>Open Export Folder</span>
                </button>
              </div>
            ) : null}

            {/* Launch Export Button */}
            <div className="space-y-3">
              <button
                disabled={isExporting}
                onClick={handleStartExport}
                className="w-full py-4 rounded-2xl bg-gradient-to-r from-brand-accent to-brand-secondary text-white font-bold text-sm shadow-glow hover:shadow-[0_0_35px_rgba(124,92,252,0.5)] transition-all flex items-center justify-center gap-2 active:scale-98 disabled:opacity-50"
              >
                {isExporting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Rendering Participant Clips...</span>
                  </>
                ) : (
                  <>
                    <Share2 className="w-4 h-4" />
                    <span>Export Selected Clips Now</span>
                  </>
                )}
              </button>

              <p className="text-[10px] text-center text-brand-muted">
                100% Offline Processing. Clips use precise FFmpeg coordinates verified by you.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
